import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import {
  IsSaveRowKeysAction,
  IsSaveArticles,
  IsToggleEditTableAction,
  IsClearTagFiltersValue,
} from '../../Constants/Configuration'
import { reorderImages, sortByPriority } from '../../Helpers/GalleryHelper'

export interface Image {
  id: Number
  url: string
  priority: Number
  type: string
}

export interface GalleryImage {
  id: Number
  priority: Number
}

export interface Gallery {
  id: Number
  name: string
  images: GalleryImage[]
}

export const imagesSlice = createSlice({
  name: 'images',
  initialState: {
    images: [] as Image[],
    galleries: [] as Gallery[],
    fetchedImages: false as boolean,
    isEdited: false,
  },
  reducers: {
    clearImages: (state, action) => {
      state.images = []
      state.galleries = []
      state.fetchedImages = false
      state.isEdited = false
    },
    resetEdit: (state, action) => {
      state.isEdited = false
    },
    setImages: (state, action) => {
      const galleries = action.payload.galleries ? [...action.payload.galleries] : []
      if (action.payload.galleries) {
        galleries.forEach(gallery => {
          gallery.images = sortByPriority(gallery.images)
        })
      }
      state.images = action.payload.images ?? []
      state.galleries = action.payload.galleries ?? []
      state.fetchedImages = true
      state.isEdited = false
    },
    addImageToPhotoBox: (state, action: PayloadAction<Image>) => {
      state.images.push(action.payload)
    },
    deleteImageFromPhotoBox: (state, action: PayloadAction<number>) => {
      const idx = state.images.findIndex(image => image.id === action.payload)
      if (idx >= 0) {
        state.images.splice(idx, 1)
        // Delete image also from galleries
        state.galleries.forEach(gallery => {
          const idx2 = gallery.images.findIndex(image => image.id === action.payload)
          if (idx2 >= 0) {
            gallery.images.splice(idx2, 1)
          }
        })
      }
    },
    addImage: (state, action) => {
      const image = state.images.find(x => x.id === action.payload.imageId)
      const gallery = state.galleries.find(x => x.id === action.payload.galleryId)
      const priority = action.payload.priority

      if (!gallery.images.find(x => x.id === action.payload.imageId)) {
        const galleryImage: GalleryImage = {
          id: image.id,
          priority: priority,
        }

        gallery.images = reorderImages(gallery.images, priority, priority, galleryImage)
      } else {
        gallery.images = reorderImages(gallery.images, action.payload.startIndex, priority)
      }

      gallery.images = gallery.images.map((x, index) => {
        x.priority = index
        return x
      })

      gallery.images = sortByPriority(gallery.images)

      state.isEdited = true
    },
    removeImage: (state, action) => {
      const gallery = state.galleries.find(x => x.id === action.payload.galleryId)

      if (gallery.images.find(x => x.id === action.payload.imageId)) {
        gallery.images = gallery.images.filter(x => x.id !== action.payload.imageId)
        state.isEdited = true
      }
    },
  },
  extraReducers: builder => {
    builder
      .addMatcher(IsSaveRowKeysAction, (state, action) => {
        if (action.payload.rowKeys.length === 0) {
          state.images = []
          state.galleries = []
          state.fetchedImages = false
        }
      })
      .addMatcher(IsSaveArticles, (state, action) => {
        if (action.payload.results.results.length !== 0 && action.payload.isEditDisable) {
          const isKeySelectedInResult =
            action.payload.results.results.filter(x => action.payload.key === x.key).length !== 0
          if (!isKeySelectedInResult) {
            state.images = []
            state.galleries = []
            state.fetchedImages = false
          }
        }

        if (action.payload.results.results.length === 0 && action.payload.isEditDisable) {
          state.images = []
          state.galleries = []
          state.fetchedImages = false
        }
      })
      .addMatcher(IsToggleEditTableAction, (state, action) => {
        state.images = []
        state.galleries = []
        state.fetchedImages = false
      })
      .addMatcher(IsClearTagFiltersValue, (state, action) => {
        state.images = []
        state.galleries = []
        state.fetchedImages = false
      })
  },
})

// Action creators are generated for each case reducer function
export const { setImages, clearImages, addImage, removeImage, resetEdit, addImageToPhotoBox, deleteImageFromPhotoBox } =
  imagesSlice.actions

export default imagesSlice.reducer
